import React from 'react';
import { motion } from 'motion/react';
import { SERVICE_COMPARISON } from '@/src/constants';
import { Check, X, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';

export default function ComparisonTable() {
  return (
    <section className="py-32 relative overflow-hidden bg-secondary/5">
      <div className="container mx-auto px-6 relative z-10">
        <div className="text-center mb-20 space-y-4">
          <p className="text-accent text-[9px] font-bold uppercase tracking-[0.4em]">Engagement Models</p>
          <h2 className="font-display text-4xl md:text-6xl font-bold tracking-tight leading-tight">
            Choose Your <br /><span className="text-accent">Defense Level</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto font-normal leading-relaxed">
            Select the engagement model that best fits your organization's risk profile and security requirements.
          </p>
        </div>

        <div className="overflow-x-auto pb-8">
          <div className="min-w-[1000px]">
            <div className="grid grid-cols-4 gap-6 mb-8">
              <div className="p-6"></div>
              {SERVICE_COMPARISON.plans.map((plan, i) => (
                <motion.div
                  key={plan.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className={`p-8 rounded-3xl relative group ${i === 1 ? 'bg-accent/5 border border-accent/20' : 'bg-white/[0.02] border border-white/5'}`}
                >
                  {i === 1 && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-accent text-background text-[8px] font-bold uppercase tracking-widest">
                      Most Common
                    </div>
                  )}
                  <div className="flex items-center gap-3 mb-4">
                    <Shield className={`w-5 h-5 ${i === 1 ? 'text-accent' : 'text-white/30'}`} />
                    <h3 className="text-xl font-display font-bold tracking-tight">{plan.name}</h3>
                  </div>
                  <div className="mb-6">
                    <p className="text-3xl font-display font-bold text-foreground">
                      {i === 0 ? "$299" : i === 1 ? "$300" : "Custom"}
                      {i === 1 && <span className="text-xs text-muted-foreground font-normal ml-1">/mo</span>}
                    </p>
                    <p className="text-[9px] text-muted-foreground uppercase tracking-widest font-bold mt-1">
                      {i === 0 ? "Fixed Price" : i === 1 ? "Starting at" : "Tailored Quote"}
                    </p>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed mb-8 h-10 font-normal">
                    {plan.description}
                  </p>
                  <Button asChild variant={i === 1 ? 'accent' : 'outline'} className="w-full rounded-xl text-[10px] font-bold uppercase tracking-[0.1em] h-12 transition-all">
                    <Link to="/contact">Get Started</Link>
                  </Button>
                </motion.div>
              ))}
            </div>

            <div className="space-y-1">
              {SERVICE_COMPARISON.features.map((feature, featureIndex) => (
                <div key={feature} className="grid grid-cols-4 gap-6 items-center group/row">
                  <div className="p-6 text-sm font-medium text-white/40 border-b border-white/5 group-hover/row:text-white transition-colors">
                    {feature}
                  </div>
                  {SERVICE_COMPARISON.plans.map((plan, planIndex) => (
                    <div key={`${plan.name}-${feature}`} className={`flex justify-center p-6 border-b border-white/5 transition-colors ${planIndex === 1 ? 'bg-accent/5' : ''}`}>
                      {plan.features[featureIndex] ? (
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center ${planIndex === 1 ? 'bg-accent text-background' : 'bg-accent/10 text-accent'}`}>
                          <Check className="w-4 h-4" />
                        </div>
                      ) : (
                        <X className="w-5 h-5 text-white/5" />
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
